// @ts-check
import { uniqueId } from './utils';

/**
 * @typedef DDPHeartBeatConfiguration
 * @property {number} [interval=2000]
 * @property {number} [countOfInflightBeforeSwitchToWaiting=2]
 * @property {number} [countOfInflightBeforeReconnectSocket=15]
 */

/** @typedef {import('./ddp').default} DDP */

export class DDPHeartBeat {
  /**
   * @param {DDP} ddp
   * @param {DDPHeartBeatConfiguration} configuration
   */
  constructor(
    ddp,
    {
      interval,
      countOfInflightBeforeSwitchToWaiting,
      countOfInflightBeforeReconnectSocket,
    } = {}
  ) {
    /**
     * @private
     * @type {DDP}
     */
    this._ddp = ddp;
    /**
     * @private
     * @type {number}
     */
    this._interval = interval || 2000;
    /**
     * @private
     * @type {number}
     */
    this._countOfInflightBeforeSwitchToWaiting =
      countOfInflightBeforeSwitchToWaiting || 2;
    /**
     * @private
     * @type {number}
     */
    this._countOfInflightBeforeReconnectSocket =
      countOfInflightBeforeReconnectSocket || 15;
    /**
     * @private
     * @type {ReturnType<typeof setInterval> | null}
     */
    this._intervalRef = null;
    /**
     * @private
     * @type {Map<string, { id: string, sentAt: number, orderId: number }>}
     */
    this._inflight = new Map();
    /**
     * @private
     * @type {number}
     */
    this._orderId = 0;
  }

  /**
   * @public
   * @returns {this}
   */
  startCircle() {
    this.stopCircle();

    this._intervalRef = setInterval(
      () => this._onHeartbeatCircle(),
      this._interval
    );

    return this;
  }

  /** @private */
  _onHeartbeatCircle() {
    if (
      this._inflight.size > this._countOfInflightBeforeSwitchToWaiting &&
      this._ddp.status === 'connected'
    ) {
      this._ddp.status = 'waiting';
    }

    // Socket is not ready, heartbeat/close packet can't be sent
    if (
      !this._ddp.socket.rawSocket ||
      this._ddp.socket.rawSocket.readyState !== 1
    ) {
      return;
    }

    if (
      this._ddp.status === 'waiting' &&
      this._inflight.size > this._countOfInflightBeforeReconnectSocket
    ) {
      console.warn(
        'MeteorRN:DDP::: Too many inflight heartbeats, seems socket is stuck, closing it to reestablish connection'
      );

      const connectionIsStuckStatus = 3001;
      this._ddp.socket.rawSocket.close(
        connectionIsStuckStatus,
        'Too many inflight heartbeats, seems socket is stuck'
      );
      return;
    }

    this._sendHeartbeat();
  }

  /**
   * @public
   * @param {{ id: string }} message
   */
  onIncomingHeartbeat({ id }) {
    const heartbeat = this._inflight.get(id);

    if (!heartbeat) {
      return;
    }

    this._inflight.delete(id);

    if (this._ddp.status === 'waiting') {
      this._ddp.status = 'connected';
    }
    if (this._inflight.size > 0) {
      // Remove all heartbeats that were sent before this one, as they are not needed anymore
      for (const { id, orderId } of this._inflight.values()) {
        if (heartbeat?.orderId > orderId) {
          this._inflight.delete(id);
        }
      }
    }
  }

  /** @private */
  _sendHeartbeat() {
    const orderId = (this._orderId += 1);

    const id = uniqueId();

    this._inflight.set(id, { sentAt: Date.now(), id, orderId });
    this._ddp.socket.send({ msg: 'ping', id });
  }

  /**
   * @public
   * @returns {this}
   */
  stopCircle() {
    if (this._intervalRef) {
      clearInterval(this._intervalRef);
      this._intervalRef = null;
    }

    this._inflight.clear();
    this._orderId = 0;

    return this;
  }
}
